import { componentApi } from ".";
import { DateTimeString } from "./date";
import { notify } from "./toast";
import { getBlankBadgePhotoBase64 } from "./visitorBadgePhoto";

const PORTS = [5000, 5001, 5002, 5003];

class VisitorManagementClient {
    private _url: string | undefined;

    initialized: boolean;

    constructor() {
        this.initialized = false;
    }

    async getUrl() {
        if (!this.initialized) {
            await this.init();
        }
            
        return this._url;
    }

    async getCurrentVersion() {
        const url = await this.getUrl();
        const response = await fetch(`${url}/sniff/version`)
        if (response.ok) {
            const version = await response.text();
            return version;
        } else {
            throw new Error(`Failed to get version: ${response.statusText}`)
        }
    }

    #latestVersion: string | null = null;
    async getLatestVersion() : Promise<string> {
        if (!this.#latestVersion) {
            this.#latestVersion = await componentApi.call('st', 'st-document-reader', 'DocumentReaderApi', 'GetVisitorManagementLatestVersion', { }) as string;
        }
        return this.#latestVersion;
    }

    async hasLatestVersion() {
        const current = await this.getCurrentVersion();
        const latest = await this.getLatestVersion();
        return current === latest;
    }

    async getStatus(): Promise<VisitorManagementBridgeStatus> {
        const url = await this.getUrl();
        if (!url)
            return { status: 'not_running' }

        const currentVersion = await this.getCurrentVersion();
        const health = await this.getHealth();
        const latestVersion = await this.getLatestVersion();
        const isLatest = currentVersion === latestVersion;

        return {
            status: isLatest ? 'running' : 'outdated',
            url,
            currentVersion,
            latestVersion,
            health,
        }
    }

    async getHealth(): Promise<'healthy' | 'unhealthy'> {
        const url = await this.getUrl();
        const response = await fetch(`${url}/health`)
        if (response.ok) {
            return 'healthy';
        } else {
            return 'unhealthy';
        }
    }

    private async init() {
        try {
            for (const port of PORTS) {
                try {
                    // Simulate port sniffing logic
                    const url = `http://localhost:${port}`
                    const response = await fetch(`${url}/sniff`, {
                        signal: AbortSignal.timeout(500),
                    });
                    if (response.ok && await response.text() === 'synergy') {
                        this._url = url;
                        break;
                    }
                } catch { }
            }
        } catch (e) {
            console.error(e)
            notify('error', 'An error occurred while trying to connect to the Synergy Visitor Management Bridge')
        } finally {
            this.initialized = true;
        }
    }

    async listScanners(): Promise<ScannerInfo[]> {
        const url = await this.getUrl();
        const response = await fetch(`${url}/scanners`)
        if (response.ok) {
            const scanners = await response.json();
            return scanners;
        } else {
            throw new Error(`Failed to list scanners: ${response.statusText}`)
        }
    }
    
    async scanDocument(scannerType: string, scannerId: string): Promise<DocumentScanResponse> {
        const url = await this.getUrl();
        const response = await fetch(`${url}/scan?scannerType=${scannerType}&id=${scannerId}`)
        if (response.ok) {
            const data: DocumentScanResponse = await response.json();

            if (data.errors && data.errors.length > 0) {
                const message = `Errors occurred while scanning document: ${data.errors.join(', ')}`;
                notify('error', message);
                throw new Error(message);
            }

            return data;
        } else {
            const error = await response.text();
            let message: string = '';
            try {
                const errorJson = JSON.parse(error);
                message = errorJson.detail || errorJson.title || 'Unknown error';
            } catch {
                message = `Error occurred while scanning document. ${error}`;
            }

            notify('error', message);
            throw new Error(message)
        } 
    }

    async listPrinters(): Promise<IPrinter[]> {
        const url = await this.getUrl();
        const response = await fetch(`${url}/printers`)
        if (response.ok) {
            const printers = await response.json();
            return printers;
        } else {
            throw new Error(`Failed to list printers: ${response.statusText}`)
        }
    }

    /**
     * POST a badge to the bridge for printing.
     *
     * Returns the bridge's own verdict, never a bare `response.ok`. `response.ok` was true for any
     * 2xx, and the bridge used to answer "Printer not found" with a 200 whose body said
     * `success: false` — so callers reported a cheerful success for a print that never happened.
     * The body is now always parsed and the server's message is handed back for display.
     *
     * Never throws for a bridge-reported failure: the caller gets `{ success: false, message }` for
     * transport errors, non-2xx responses, and `success: false` bodies alike.
     */
    async print(req: PrintRequest): Promise<PrintResult> {
        let response: Response;
        try {
            const url = await this.getUrl();
            if (!url)
                return { success: false, message: 'The Synergy Visitor Management Bridge is not running on this machine.' };

            response = await fetch(`${url}/print`, {
                method: 'POST',
                body: JSON.stringify(withBadgePhoto(req)),
                headers: { 'Content-Type': 'application/json' },
            });
        } catch (e) {
            console.error(e);
            return { success: false, message: 'Could not reach the Synergy Visitor Management Bridge.' };
        }

        // Parse the body regardless of status: the bridge reports the reason there on both paths.
        let body: { success?: boolean; message?: string } | undefined;
        try {
            body = await response.json();
        } catch {
            body = undefined;
        }

        if (!response.ok) {
            return {
                success: false,
                message: body?.message || `The print request failed (HTTP ${response.status} ${response.statusText}).`,
            };
        }

        // A 2xx with an explicit success:false is still a failure.
        if (body && body.success === false) {
            return { success: false, message: body.message || 'The printer reported a failure.' };
        }

        return { success: true, message: body?.message };
    }

    async getMachineInfo(): Promise<MachineInfo> {
        const url = await this.getUrl();
        if (!url) throw new Error('Visitor Management Bridge is not running.');
        const response = await fetch(`${url}/machine-info`);
        if (response.ok) return await response.json();
        throw new Error(`Failed to get machine info: ${response.statusText}`);
    }
}

export type MachineInfo = {
    macAddress: string;
    machineName: string;
}

export type VisitorManagementBridgeStatus = {
    status: 'not_running' | 'running' | 'outdated';
    url?: string;
    currentVersion?: string;
    latestVersion?: string;
    health?: 'healthy' | 'unhealthy';
}

export const visitorManagementClient = new VisitorManagementClient();

export type AAMVAInfo = {
    additionalJurisdictionElements: Record<string, string>;
    city?: string;
    country?: string;
    state?: string;
    zip?: string;
    street1?: string;
    street2?: string;
    dateOfBirth: DateTimeString;
    documentDiscriminator?: string;
    ethnicity?: string;
    expirationDate: DateTimeString;
    eyeColor?: string;
    hairColor?: string;
    heightInches: number;
    idNumber?: string;
    inventoryControlNumber?: string;
    isOrganDonor: boolean;
    issueDate: DateTimeString;
    issuerIdentificationNumber?: string;
    endorsementCodeDescription?: string;
    endorsementCodes?: string;
    restrictionCodeDescription?: string;
    restrictionCodes?: string;
    vehicleClass?: string;
    vehicleClassificationDescription?: string;
    aliasFirst?: string;
    aliasLast?: string;
    aliasSuffix?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    suffix?: string;
    placeOfBirth?: string;
    revisionDate: DateTimeString;
    sex?: string;
    weightPounds: number;
}

export type ScannerInfo = {
    id: string;
    displayName: string;
    type: string;
}

export type DocumentScanResponse = {
    aamvaInfo: AAMVAInfo;
    front: string; // base64
    back: string;
    photo: string;
    errors: string[];
}

export interface IPrinter {
    name: string;
}

export interface PrintRequest {
    PrinterName: string;
    Data: DocumentPrintData
}

/**
 * Outcome of a badge print as reported by the bridge.
 *
 * `success: true` means the bridge handed the job to the printer's Windows queue — it is NOT proof
 * that a label physically emerged. There is no completion signal past the spooler handoff.
 */
export interface PrintResult {
    success: boolean;
    /** Server-supplied reason, suitable for showing to the operator. Present on most failures. */
    message?: string;
}

/**
 * Guarantees the badge carries a photo image, so the label template's placeholder photo can never
 * print. See `visitorBadgePhoto.ts` for why an empty image is not good enough.
 *
 * Applied at the one call that reaches the bridge, so every print path is covered — the check-in
 * wizard, Reprint from the grid, and anything added later.
 *
 * Returns a copy. `Data` is a Solid store on the wizard path and must not be mutated.
 */
function withBadgePhoto(req: PrintRequest): PrintRequest {
    if (req.Data?.ImageBase64?.trim())
        return req;

    return { ...req, Data: { ...req.Data, ImageBase64: getBlankBadgePhotoBase64() } };
}

export interface DocumentPrintData {
    VisitorType: string;
    FirstName: string;
    LastName: string;
    VisitorBarCode: string;
    Reason: string;
    MeetingWith: string;
    CheckInDateTime: string;
    GUID: string;
    ImageBase64: string;
}

export const thalesErrorMap: Record<string, string> = {
    NO_ERROR_OCCURRED: "No error occurred.",
    UNKNOWN_ERROR_OCCURRED: "An unknown error occurred.",
    ERROR_FEATURE_NOT_ENABLED: "This feature is not enabled.",
    ERROR_FEATURE_NOT_SUPPORTED: "This feature is not supported.",
    ERROR_NOT_INITIALISED: "The system is not initialised.",
    ERROR_ALREADY_INITIALISED: "The system is already initialised.",
    ERROR_INITIALISATION_FAILED: "Initialisation failed.",
    ERROR_DEVICE_SUSPENDED: "The device is suspended.",
    ERROR_DEVICE_ALREADY_IN_USE: "The device is already in use.",
    ERROR_READER_NOT_CONNECTED: "The reader is not connected.",
    ERROR_UNKNOWN_DATA_FORMAT: "Unknown data format.",
    ERROR_FILE_DOES_NOT_EXIST: "The file does not exist.",
    ERROR_READING_FILE: "Failed to read file.",
    ERROR_WRITING_FILE: "Failed to write file.",
    ERROR_CANNOT_OBTAIN_VERSION: "Cannot obtain version information.",
    ERROR_CANNOT_OBTAIN_MODULE_INFO: "Cannot obtain module information.",
    ERROR_CANNOT_LOCATE_SERIAL: "Cannot locate serial device.",
    ERROR_LOADING_DLL: "Failed to load DLL.",
    ERROR_NO_ROOT_INI_FILE: "Root INI file is missing.",
    ERROR_INVALID_CUSTOM_SETTINGS_PATH: "Invalid custom settings path.",
    ERROR_INVALID_CONFIG_FILE_FORMAT: "Invalid configuration file format.",
    ERROR_OS_ERROR: "Operating system error.",
    ERROR_ALLOCATING_MEMORY: "Memory allocation failed.",
    ERROR_PARAMETER_INVALID: "Invalid parameter.",
    ERROR_INDEX_OUT_OF_BOUNDS_ERROR: "Index out of bounds.",
    ERROR_STRING_BUFFER_TOO_SMALL: "String buffer too small.",
    ERROR_DATA_BUFFER_TOO_SMALL: "Data buffer too small.",
    ERROR_IMAGE_TOO_SMALL: "Image is too small.",
    ERROR_IMAGE_WRONG_FORMAT: "Wrong image format.",
    ERROR_IMAGE_LOCKBITS_FAILED: "Image lockbits failed.",
    ERROR_TIMED_OUT: "Operation timed out.",
    ERROR_CURRENTLY_IN_USE: "Resource is currently in use.",
    ERROR_OPERATION_CANCELLED: "Operation was cancelled.",
    ERROR_ALREADY_STARTED: "Operation already started.",
    ERROR_NOT_STARTED: "Operation not started.",
    ERROR_CAMERA_NOT_FOUND: "Camera not found.",
    ERROR_OPENING_CAMERA: "Failed to open camera.",
    ERROR_CAMERA_DRIVER_ERROR: "Camera driver error.",
    ERROR_PREVIOUS_CAMERA_DRIVER_ERROR: "A prior camera driver error was detected.",
    ERROR_CAMERA_AND_SETTINGS_MISMATCH: "Camera and settings mismatch.",
    ERROR_WRONG_SIZE_SETTINGS_BUFFER: "Settings buffer has wrong size.",
    ERROR_USB1_NOT_SUPPORTED: "USB 1.1 is not supported.",
    ERROR_INCORRECT_DRIVERS: "Incorrect drivers installed.",
    ERROR_GPIO: "GPIO error.",
    ERROR_RF_DEVICE_NOT_FOUND: "RF device not found.",
    ERROR_UNRECOGNISED_DEVICE: "Unrecognised device.",
    ERROR_DEVICE_NOT_PRESENT: "Device is not present.",
    ERROR_CANNOT_DISABLE_DEVICE: "Cannot disable device.",
    ERROR_CANNOT_ENABLE_DEVICE: "Cannot enable device.",
    ERROR_VOLUME_MISSING: "Required volume is missing.",
    ERROR_NO_AVAILABLE_DRIVES: "No available drives.",
    ERROR_CANNOT_GET_DEVICE_DETAILS: "Cannot get device details.",
    ERROR_UNKNOWN_USB_REPORT: "Unknown USB report.",
    ERROR_GETTING_USB_VALUE: "Failed to get USB value.",
    ERROR_CAMERA_DEVICE_DISABLED: "Camera device is disabled.",
    ERROR_RF_BIOCHIP_DEAD: "RF biochip not responding.",
    ERROR_RF_INVALID_DATAGROUP: "RF: invalid data group.",
    ERROR_RF_SELECT_APP_FAILED: "RF: failed to select application.",
    ERROR_RF_DG_NOT_PRESENT: "RF: data group not present.",
    ERROR_RF_GET_DATA_ITEM_FAILED: "RF: failed to get data item.",
    ERROR_RF_VALIDATE_DATA_ITEM_FAILED: "RF: data item validation failed.",
    ERROR_RF_DECODE_DATA_ITEM_FAILED: "RF: data item decode failed.",
    ERROR_RF_DECODE_DATA_ITEM_EMPTY: "RF: data item is empty.",
    ERROR_RF_BAC_FAILURE: "RF: BAC (Basic Access Control) failed.",
    ERROR_RF_ACTIVE_AUTHENTICATION_FAILURE: "RF: active authentication failed.",
    ERROR_RF_SIGNED_ATTRS_FAILED: "RF: signed attributes validation failed.",
    ERROR_RF_SIGNATURE_FAILED: "RF: signature validation failed.",
    ERROR_RF_READER_DEAD: "RF reader not responding.",
    ERROR_RF_APDU_NO_TAG_IN_FIELD: "RF: no tag in field.",
    ERROR_RF_APDU_NO_RESPONSE: "RF: APDU no response.",
    ERROR_RF_APDU_CRC_FAILURE: "RF: APDU CRC failure.",
    ERROR_RF_APDU_GENERAL_FAILURE: "RF: APDU general failure.",
    ERROR_RF_APDU_ERR_RESPONSE: "RF: APDU error response.",
    ERROR_RF_APDU_UNKNOWN_COMMAND: "RF: APDU unknown command.",
    ERROR_RF_APDU_OP_MODE_FAILURE: "RF: APDU operation mode failure.",
    ERROR_RF_APDU_PARAM_OUT_OF_RANGE: "RF: APDU parameter out of range.",
    ERROR_RF_APDU_ACCESS_DENIED: "RF: APDU access denied.",
    ERROR_RF_APDU_UNKNOWN_ERROR: "RF: APDU unknown error.",
    ERROR_RF_DECODE_EF_COM_FAILURE: "RF: failed to decode EF.COM.",
    ERROR_RF_DECODE_EF_SOD_FAILURE: "RF: failed to decode EF.SOD.",
    ERROR_RF_DECODE_EF_CHIP_SECURITY_FAILURE: "RF: failed to decode EF.ChipSecurity.",
    ERROR_RF_DECODE_EF_CARD_SECURITY_FAILURE: "RF: failed to decode EF.CardSecurity.",
    ERROR_RF_DECODE_EF_CARD_ACCESS_FAILURE: "RF: failed to decode EF.CardAccess.",
    ERROR_RF_CHIP_AUTHENTICATION_FAILED: "RF: chip authentication failed.",
    ERROR_RF_CERTS_LOAD_FAILED: "RF: failed to load certificates.",
    ERROR_RF_TERMINAL_AUTHENICATION_FAILED: "RF: terminal authentication failed.",
    ERROR_RF_UNAUTHORISED_SENSITIVE_DG: "RF: access to sensitive data group not authorised.",
    ERROR_RF_NO_CHIP_SELECTED: "RF: no chip selected.",
    ERROR_RF_ABORTED: "RF: operation aborted.",
    ERROR_RF_READER_INDEX_UNKNOWN: "RF: unknown reader index.",
    ERROR_OPENING_COM_PORT: "Failed to open COM port.",
    ERROR_SETTING_COM_PORT_TIMEOUTS: "Failed to set COM port timeouts.",
    ERROR_SETTING_COM_PORT: "Failed to configure COM port.",
    ERROR_CLOSING_COM_PORT: "Failed to close COM port.",
    ERROR_READING_COM_PORT: "Failed to read from COM port.",
    ERROR_WRITING_COM_PORT: "Failed to write to COM port.",
    ERROR_CONNECTING_DEVICE: "Failed to connect to device.",
    ERROR_DISCONNECTING_DEVICE: "Failed to disconnect device.",
    ERROR_SWIPE_READER_NOT_CONNECTED: "Swipe reader not connected.",
    ERROR_MSR_NOT_CONNECTED: "MSR not connected.",
    ERROR_SW_UNKNOWN_PROTOCOL: "Swipe: unknown protocol.",
    ERROR_SW_NO_SERIAL_DATA: "Swipe: no serial data.",
    ERROR_SW_COMMAND_PARAMETER_INVALID: "Swipe: invalid command parameter.",
    ERROR_SW_UNKNOWN_PROTOCOL_COMMAND: "Swipe: unknown protocol command.",
    ERROR_SW_BAD_CHARACTER: "Swipe: bad character in stream.",
    ERROR_SW_CANNOT_PROCESS_COMMANDS: "Swipe: cannot process commands.",
    ERROR_SW_INVALID_PROTOCOL_COMMAND: "Swipe: invalid protocol command.",
    ERROR_SW_PROTOCOL_COMMAND_FAILED: "Swipe: protocol command failed.",
    ERROR_SW_COMMAND_NOT_ACKNOWLEDGED: "Swipe: command not acknowledged.",
    ERROR_SW_RTE_SOH_MISSING: "Swipe/RTE: SOH missing.",
    ERROR_SW_RTE_INCORRECT_MESSAGE_LENGTH: "Swipe/RTE: incorrect message length.",
    ERROR_SW_RTE_STX_MISSING: "Swipe/RTE: STX missing.",
    ERROR_SW_RTE_ETX_MISSING: "Swipe/RTE: ETX missing.",
    ERROR_SW_RTE_NO_MESSAGE_CONTENT: "Swipe/RTE: no message content.",
    ERROR_SW_RTE_BAD_MESSAGE_CHECKSUM: "Swipe/RTE: bad message checksum.",
    ERROR_SW_RTE_BAD_TRACK_CHECKSUM: "Swipe/RTE: bad track checksum.",
    ERROR_SW_RTE_BAD_BLOCK_CHECKSUM: "Swipe/RTE: bad block checksum.",
    ERROR_SW_RTE_QA_STX_MISSING: "Swipe/RTE QA: STX missing.",
    ERROR_SW_RTE_QA_ETX_MISSING: "Swipe/RTE QA: ETX missing.",
    ERROR_SW_RTE_QA_NO_MESSAGE_CONTENT: "Swipe/RTE QA: no message content.",
    ERROR_SW_RTE_QA_INCORRECT_LINE_COUNT: "Swipe/RTE QA: incorrect line count.",
    ERROR_SW_RTE_QA_INCORRECT_PARAM_COUNT: "Swipe/RTE QA: incorrect parameter count.",
    ERROR_SW_MUSE_STX_MISSING: "Swipe/MUSE: STX missing.",
    ERROR_SW_MUSE_ETX_MISSING: "Swipe/MUSE: ETX missing.",
    ERROR_SW_MUSE_NO_MESSAGE_CONTENT: "Swipe/MUSE: no message content.",
    ERROR_SW_MUSE_UNKNOWN_DEVICE_TYPE: "Swipe/MUSE: unknown device type.",
    ERROR_SW_MUSE_BAD_CRC16: "Swipe/MUSE: bad CRC16.",
    ERROR_SW_CUTE_SOD_MISSING: "Swipe/CUTE: SOD missing.",
    ERROR_SW_CUTE_STX_MISSING: "Swipe/CUTE: STX missing.",
    ERROR_SW_CUTE_ETX_MISSING: "Swipe/CUTE: ETX missing.",
    ERROR_SW_CUTE_EOD_MISSING: "Swipe/CUTE: EOD missing.",
    ERROR_SW_CUTE_NO_MESSAGE_CONTENT: "Swipe/CUTE: no message content.",
    ERROR_SW_CUTE_UNKNOWN_DEVICE_TYPE: "Swipe/CUTE: unknown device type.",
    ERROR_SW_MAGMSR_TRACK_ERROR: "Swipe/MAGMSR: track read error.",
    ERROR_UHF_READER_DEAD: "UHF reader not responding.",
    ERROR_UHF_READ_TAGID: "UHF: failed to read tag ID.",
    ERROR_UHF_READ_EPC: "UHF: failed to read EPC.",
    ERROR_UHF_READ_MEMORY_BANK: "UHF: failed to read memory bank.",
    ERROR_UHF_READ_ERROR: "UHF: read error.",
    ERROR_UHF_TAG_MEMORY_MAP_UNKNOWN: "UHF: unknown tag memory map.",
    ERROR_UHF_TAG_LOST: "UHF: tag lost.",
    ERROR_DECODE_ERROR: "Decode error.",
    ERROR_INVALID_STATE_CHANGE: "Invalid state change.",
    ERROR_BLOCKING_ONLY: "Operation allowed only in blocking mode.",
    ERROR_MID_STATECHANGE: "Operation mid-state change.",
    ERROR_NO_DOC_ON_WINDOW: "No document on window.",
    ERROR_MMMR_SETTNGS_NOT_FOUND: "MMMR settings not found.",
    ERROR_MEMORY_PARSING_ERROR: "Memory parsing error.",
    ERROR_UNINITIALISED_EEPROM: "EEPROM is uninitialised.",
    ERROR_NO_MOTOR_FEED_CALL_ISSUED: "No motor feed call issued.",
    ERROR_PLUGIN_ERROR: "Plugin error.",
    ERROR_PARAMETER_INCORRECT: "Incorrect parameter.",
    ERROR_LAYOUT_LOAD_FAILURE: "Failed to load layout.",
    ERROR_UNKNOWN_DATA_ITEM: "Unknown data item.",
    ERROR_LAYOUT_DATABASE_ISSUE: "Layout database issue.",
    ERROR_IMAGE_NOT_AVAILABLE: "Image not available.",
    ERROR_CANNOT_FIND_LAYOUT_OBJECT: "Cannot find layout object.",
    ERROR_INVALID_STATE_FOR_REQUEST: "Invalid state for this request.",
    ERROR_LICENSE_FILE_DOES_NOT_EXIST: "License file does not exist.",
    ERROR_NO_LICENSE_FILE_LOADED: "No license file loaded.",
    ERROR_LICENSE_FILE_EXPIRED: "License file has expired.",
    ERROR_LICENSE_FILE_INVALID: "License file is invalid.",
    ERROR_LICENSE_CUSTOMER_NUMBER_INVALID: "License customer number is invalid.",
    ERROR_LICENSE_COMPUTER_NAME_INVALID: "License computer name is invalid.",
    ERROR_LICENSE_MACHINE_FINGERPRINT_INVALID: "License machine fingerprint is invalid.",
    ERROR_OCR_FAILURE: "OCR failed.",
    ERROR_GET_DATA_ITEM_FAILURE: "Failed to get data item.",
    ERROR_INSUFFICIENT_PERMISIONS: "Insufficient permissions.",
    ERROR_PLUGABLE_ERROR: "Pluggable component error.",
    ERROR_BARCODE_PLUGIN_UNAVAILABLE: "Barcode plugin unavailable.",
    ERROR_NO_FLIPSIDE_AVAILABLE: "No flipside available.",
    ERROR_INVALID_STATE_FOR_DATA_RETRIEVAL: "Invalid state for data retrieval.",
    ERROR_READING_REFERENCE_FILE_PROCESS: "Error reading reference file (process).",
    ERROR_READING_REFERENCE_FILE_IDENTIFIER: "Error reading reference file (identifier).",
    ERROR_INCORRECT_PIXEL_FORMAT_PROCESS: "Incorrect pixel format (process).",
    ERROR_INCORRECT_PIXEL_FORMAT_IDENTIFIER: "Incorrect pixel format (identifier).",
    ERROR_REFERENCE_FILE_USAGE: "Reference file usage error.",
    ERROR_CREATING_EIGEN_REFERENCE_FILE: "Failed to create Eigen reference file.",
    ERROR_READING_EIGEN_REFERENCE_FILE: "Failed to read Eigen reference file.",
    ERROR_WRITING_EIGEN_REFERENCE_FILE: "Failed to write Eigen reference file.",
    ERROR_MISMATCHING_EIGEN_REFERENCE_FILE: "Mismatching Eigen reference file.",
    ERROR_CREATING_EIGEN_MATRIX: "Failed to create Eigen matrix.",
    ERROR_READING_EIGEN_MATRIX: "Failed to read Eigen matrix.",
    ERROR_CREATING_EIGEN_COEFFICIENT: "Failed to create Eigen coefficient.",
    ERROR_CALCULATING_EIGEN_VECTOR: "Failed to calculate Eigen vector.",
    ERROR_LOADING_IMAGE_INTO_OCR_TOOLKIT: "Failed to load image into OCR toolkit.",
    ERROR_PARAMETER_INVALID_PROCESS: "Invalid parameter (process).",
    ERROR_PARAMETER_INVALID_IDENTIFIER: "Invalid parameter (identifier).",
    ERROR_PARAMETER_INVALID_VERIFIER: "Invalid parameter (verifier).",
    ERROR_SEARCH_ZONE_INCORRECT: "Search zone is incorrect.",
    ERROR_REFERENCE_FILE_LEARN: "Reference file learning error.",
    ERROR_REFERENCE_FILE_REDUCTION: "Reference file reduction error.",
    ERROR_REFERENCE_PIXEL_FORMAT_PROCESS: "Reference pixel format error (process).",
    ERROR_REFERENCE_PIXEL_FORMAT_IDENTIFIER: "Reference pixel format error (identifier).",
    ERROR_SUBIMAGE_EXTRACT: "Failed to extract subimage.",
    ERROR_SW_TECS_GS_MISSING: "Swipe/TECS: GS missing.",
    ERROR_SW_TECS_EOT_MISSING: "Swipe/TECS: EOT missing.",
    ERROR_SW_TECS_NO_MESSAGE_CONTENT: "Swipe/TECS: no message content.",
    ERROR_SW_TECS_UNKNOWN_DEVICE_TYPE: "Swipe/TECS: unknown device type.",
    ERROR_FACE_MATCH_INVALID_PRODUCT_KEY: "Face match: invalid product key.",
    ERROR_FACE_MATCH_PRODUCT_KEY_EXPIRED: "Face match: product key expired.",
    ERROR_FACE_MATCH_GENERAL_FAILURE: "Face match: general failure.",
    ERROR_MDL_INITIALIZATION_FAILED: "MDL: initialization failed.",
    ERROR_MDL_CONNECTION_TIMEOUT: "MDL: connection timed out.",
    ERROR_MDL_CONNECTION_DISCONNECTED: "MDL: connection disconnected.",
    ERROR_MDL_DATA_DECODE_ERROR: "MDL: data decode error.",
    ERROR_LICENSE_TRIAL_EXPIRED: "License trial period expired.",
    ERROR_LICENSE_FEATURE_MISSING: "Required license feature is missing.",
    NUM_ERROR_CODES: "Number of defined error codes.",
    ERROR_MISMATCH_IN_AN_ENUM: "Mismatch detected in an enum.",
    CUSTOM_ERROR_CODE_MIN: "Reserved range: minimum custom error code.",
    CUSTOM_ERROR_CODE_MAX: "Reserved range: maximum custom error code.",
}